import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import type { FixedAssetsResult, FixedAssetsSnapshot } from "@ranfac/shared";
import { downloadFixedAssetsJe, fetchFixedAssets } from "../api.js";
import { WorkpaperActions } from "../components/WorkpaperActions.js";

export function FixedAssets() {
  const { period = "2026-01" } = useParams();
  const periodEnd = periodEndOf(period);
  const [data, setData] = useState<{
    snapshot: FixedAssetsSnapshot;
    result: FixedAssetsResult;
    source: "upload" | "fixture";
  } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetchFixedAssets(periodEnd)
      .then(setData)
      .catch((e) => setErr(String(e)));
  }, [periodEnd]);

  if (err) return <div className="banner warn">Could not load: {err}</div>;
  if (!data) return <div className="card">Loading…</div>;

  const { result, source } = data;

  async function onJe() {
    setBusy(true);
    try {
      const { filename, csv } = await downloadFixedAssetsJe(periodEnd);
      const blob = new Blob([csv], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      setToast(`Downloaded ${filename}.`);
    } catch (e) {
      setToast(`JE failed: ${e}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="card">
        <Link to={`/close/${period}`}>&larr; back to close dashboard</Link>
        <h1>Fixed Assets & Depreciation</h1>
        <div className="subtle">
          Eight asset classes · period ending {result.periodEnd} ·{" "}
          {source === "upload" ? (
            <span
              className="badge"
              style={{ background: "#dcfce7", color: "#166534" }}
            >
              uploaded file
            </span>
          ) : (
            <span className="badge warn">
              sample data — upload FAS Depreciation Expense Report to use live
              numbers
            </span>
          )}
        </div>
        <div className="meta" style={{ marginTop: 16 }}>
          <div className="item">
            <div className="label">Sage source</div>
            <div>FA_AssetAccount + FAS Depreciation Expense Report</div>
          </div>
          <div className="item">
            <div className="label">Calculation</div>
            <div>
              Monthly depreciation + amortization from FAS roll-up per asset
              class. JE posts to 5610/5620/5630/5660/6680 with matching accum
              depreciation credits.
            </div>
          </div>
          <div className="item">
            <div className="label">Phase</div>
            <div>2 · MVP</div>
          </div>
        </div>
      </div>

      <div className="kpi-grid">
        <Kpi label="Cost basis (end of period)" value={fmt(result.totalCostBasis)} />
        <Kpi label="Accum. depreciation" value={fmt(result.totalAccumDepreciation)} />
        <Kpi label="Net fixed assets" value={fmt(result.totalNet)} />
        <Kpi label="Monthly charge" value={fmt(result.monthlyCharge)} />
      </div>

      <div className="toolbar">
        <button onClick={onJe} disabled={busy || result.monthlyCharge === 0}>
          Generate JE (CSV)
        </button>
      </div>

      <WorkpaperActions reconId="fixed-assets" period={period} />

      <div className="card">
        <h2>Per-class rollforward</h2>
        <div style={{ overflowX: "auto" }}>
          <table>
            <thead>
              <tr>
                <th>Asset Class</th>
                <th className="num">Cost (prior)</th>
                <th className="num">Additions</th>
                <th className="num">Cost (end)</th>
                <th className="num">Accum (prior)</th>
                <th className="num">Accum (end)</th>
                <th className="num">Depreciation</th>
                <th className="num">Amortization</th>
              </tr>
            </thead>
            <tbody>
              {result.rows.map((r) => (
                <tr key={r.assetClass}>
                  <td>{r.assetClass}</td>
                  <td className="num">{fmt(r.costPriorPeriod)}</td>
                  <td className="num">
                    {r.costChange === 0 ? "—" : fmt(r.costChange)}
                  </td>
                  <td className="num">{fmt(r.costCurrentPeriod)}</td>
                  <td className="num">{fmt(r.accumPriorPeriod)}</td>
                  <td className="num">{fmt(r.accumCurrentPeriod)}</td>
                  <td className="num">
                    {r.monthlyDepreciation === 0
                      ? "—"
                      : fmt(r.monthlyDepreciation)}
                  </td>
                  <td className="num">
                    {r.monthlyAmortization === 0
                      ? "—"
                      : fmt(r.monthlyAmortization)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {toast && (
        <div className="toast" onClick={() => setToast(null)}>
          {toast}
        </div>
      )}
    </>
  );
}

function Kpi({
  label,
  value,
}: {
  label: string;
  value: string | number;
}) {
  return (
    <div className="kpi">
      <div className="label">{label}</div>
      <div className="value">{value}</div>
    </div>
  );
}

function fmt(n: number): string {
  return n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function periodEndOf(period: string): string {
  const [y, m] = period.split("-");
  const year = Number(y);
  const month = Number(m);
  const last = new Date(Date.UTC(year, month, 0));
  return last.toISOString().slice(0, 10);
}
